import _ from 'lodash'

// The MIT License (MIT)
//
//  Copyright (c) 2014 Jon Eisen
//
//  Permission is hereby granted, free of charge, to any person obtaining a copy
//  of this software and associated documentation files (the "Software"), to deal
//  in the Software without restriction, including without limitation the rights
//  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
//  copies of the Software, and to permit persons to whom the Software is
//  furnished to do so, subject to the following conditions:
//
//  The above copyright notice and this permission notice shall be included in all
//  copies or substantial portions of the Software.
//
//  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
//  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
//  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
//  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
//  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
//  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
//  SOFTWARE.
//

// Best place to find information on XHR features is:
// https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest

let reqfields = ['responseType', 'withCredentials', 'timeout', 'onprogress']

// Simple and small ajax function
// Takes a parameters object and a callback function
// Parameters:
//  - url: string, required
//  - headers: object of `{header_name: header_value, ...}`
//  - body:
//      + string (sets content type to 'application/x-www-form-urlencoded' if not set in headers)
//      + FormData (doesn't set content type so that browser will set as appropriate)
//  - method: 'GET', 'POST', etc. Defaults to 'GET' or 'POST' based on body
//  - cors: If your using cross-origin, you will need this true for IE8-9
//
// The following parameters are passed onto the xhr object.
// IMPORTANT NOTE: The caller is responsible for compatibility checking.
//  - responseType: string, various compatability, see xhr docs for enum options
//  - withCredentials: boolean, IE10+, CORS only
//  - timeout: long, ms timeout, IE8+
//  - onprogress: callback, IE10+
//
// Callback function prototype:
//  - statusCode from request
//    + Possibly null or 0 (i.e. falsy) if an error occurs
//  - response
//    + if responseType set and supported by browser, this is an object of some type (see docs)
//    + otherwise if request completed, this is the string text of the response
//    + if request is aborted, this is "Abort"
//    + if request times out, this is "Timeout"
//    + if request errors before completing (probably a CORS issue), this is "Error"
//  - request object
//
// Returns the request object. So you can call .abort() or other methods
//
// DEPRECATIONS:
//  - Passing a string instead of the params object has been removed!
//

function getRequest(cors) {
   // XDomainRequest is only way to do CORS in IE 8 and 9
   // But XDomainRequest isn't standards-compatible
   // Notably, it doesn't allow cookies to be sent or set by servers
   // IE 10+ is standards-compatible in its XMLHttpRequest
   // but IE 10 can still have an XDomainRequest object, so we don't want to use it
   if (cors && window.XDomainRequest && !/MSIE 1/.test(navigator.userAgent)) {
      return new XDomainRequest()
   }
   if (window.XMLHttpRequest) {
      return new XMLHttpRequest()
   }

   throw new Error('no valid XHR object')
}

function setDefault(obj, key, value) {
   obj[key] = obj[key] || value
}

const ajax = function(params, callback) {
   // Any variable used more than once is var'd here because
   // minification will munge the variables whereas it can't munge
   // the object access.
   let headers = params.headers || {},
      body = params.body,
      method = params.method || (body ? 'POST' : 'GET'),
      called = false

   let req = getRequest(params.cors)

   function cb(statusCode, responseText) {
      return function() {
         if (!called) {
            callback(
               req.status === undefined ? statusCode : req.status,
               0 === req.status ? 'Error' : req.response || req.responseText || responseText,
               req,
            )
            called = true
         }
      }
   }

   req.open(method, params.url, true)

   let success = (req.onload = cb(200))

   req.onreadystatechange = function() {
      if (4 === req.readyState) {
         success()
      }
   }
   req.onerror = cb(null, 'Error')
   req.ontimeout = cb(null, 'Timeout')
   req.onabort = cb(null, 'Abort')

   if (body) {
      setDefault(headers, 'X-Requested-With', 'XMLHttpRequest')

      if (!window.FormData || !(body instanceof window.FormData)) {
         setDefault(headers, 'Content-Type', 'application/json')
      }
   }

   for (let i = 0, len = reqfields.length, field; i < len; i++) {
      field = reqfields[i]
      if (params[field] !== undefined) {
         req[field] = params[field]
      }
   }

   for (let field in headers) {
      if (field) {
         req.setRequestHeader(field, headers[field])
      }
   }

   console.log('request ' + method + ' ' + params.url)
   req.send(body)

   return req
}

const req = (method, url, body, headers) =>
   new Promise((resolve, reject) =>
      ajax(
         {
            url,
            body,
            headers: _.assign({}, headers || {}),
            method:  method.toUpperCase(),
            cors:    true
         },
         (code, resp, httpreq) => {
            const contentType = httpreq.getResponseHeader('content-type'),
               parseAsJson = contentType ? contentType.match(/\/json/) : false

            let err,
               data = resp

            if (!code || 400 <= code) {
               try {
                  data = parseAsJson ? JSON.parse(data) : data
               } catch (e) {
                  // void
               }

               err = new Error(resp)

               err.data = data
               err.req = httpreq

               return reject(err)
            } else {
               try {
                  if (data && parseAsJson) {
                     return resolve([JSON.parse(data), httpreq])
                  } else {
                     return resolve([data, httpreq])
                  }
               } catch (e) {
                  e.data = data
                  e.req = httpreq

                  return reject(e)
               }
            }
         },
      ),
   )

req.get = (url, body, headers) => req('GET', url, null, headers)
req.put = (url, body, headers) => req('PUT', url, body, headers)
req.post = (url, body, headers) => req('POST', url, body, headers)
req.delete = (url, body, headers) => req('DELETE', url, body, headers)

export {ajax, req}



// WEBPACK FOOTER //
// lib/nanoajax.js